import { useCallback, useEffect, useState } from 'react';
import { useTrip } from '../lib/tripContext.jsx';
import { useSession } from '../lib/sessionContext.jsx';
import { fetchTars, saveTars, previewTars } from '../lib/tarsApi.js';

// Player Settings tab — currently houses the TARS calibration panel.
// Future home for any per-player preference (push schedule, daily-
// brief opt-out, etc). Surfaced from More menu's CAPTURE section.
//
// TARS controls the tonal dial Wilman applies to DYNAMIC content
// directed at this player (verdicts, daily briefs, EXIF critiques,
// headliner commentary). Static pre-rendered audio (photo coaching,
// restaurant intros, welcome briefings, pre-trip nudges) uses the
// team default and is unaffected by per-player settings.

export default function Settings() {
  const { tripId, trip } = useTrip();
  const session = useSession();
  const meId = session?.role === 'player' ? session.recipient_id : null;
  const me = (trip?.players || []).find((p) => p.id === meId);

  return (
    <div className="mx-auto w-full max-w-screen-sm space-y-4 p-4">
      <header>
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-fg-tertiary">
          per-player preferences
        </p>
        <h1 className="mt-1 font-serif text-2xl">TARS Settings</h1>
        {me && (
          <p className="mt-1 text-sm text-fg-secondary">
            Signed in as <span className="font-medium text-fg-primary">{me.display_name || me.name}</span>.
          </p>
        )}
      </header>

      {!session.loading && !meId && (
        <div className="card border-copper/40 bg-copper/5 px-4 py-3">
          <p className="font-mono text-[10px] uppercase tracking-wider text-copper-dark">
            sign-in required
          </p>
          <p className="mt-1 text-sm text-fg-secondary">
            Open from your briefing-email link to access your settings.
          </p>
        </div>
      )}

      {meId && <TarsPanel tripId={tripId} />}
    </div>
  );
}

function TarsPanel({ tripId }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [defaults, setDefaults] = useState({ humor: 75, honesty: 95, sarcasm: 60 });
  const [source, setSource] = useState('default');
  const [humor, setHumor] = useState(75);
  const [honesty, setHonesty] = useState(95);
  const [sarcasm, setSarcasm] = useState(60);
  const [savingState, setSavingState] = useState('idle'); // 'idle' | 'saving' | 'saved'
  const [previewState, setPreviewState] = useState('idle'); // 'idle' | 'rendering' | 'ready' | 'error'
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewError, setPreviewError] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchTars({ tripId });
      setDefaults(res.defaults || defaults);
      setSource(res.source || 'default');
      setHumor(res.tars?.humor ?? 75);
      setHonesty(res.tars?.honesty ?? 95);
      setSarcasm(res.tars?.sarcasm ?? 60);
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripId]);

  useEffect(() => {
    reload();
  }, [reload]);

  async function onSave() {
    setSavingState('saving');
    setError(null);
    try {
      await saveTars({ tripId, humor, honesty, sarcasm });
      setSource('saved');
      setSavingState('saved');
      setTimeout(() => setSavingState('idle'), 1500);
    } catch (err) {
      setSavingState('idle');
      setError(err.message || String(err));
    }
  }

  async function onPreview() {
    setPreviewState('rendering');
    setPreviewError(null);
    setPreviewUrl(null);
    try {
      const res = await previewTars({ tripId, humor, honesty, sarcasm });
      setPreviewUrl(res.url);
      setPreviewState('ready');
    } catch (err) {
      setPreviewState('error');
      setPreviewError(err.message || String(err));
    }
  }

  function onResetToDefault() {
    setHumor(defaults.humor);
    setHonesty(defaults.honesty);
    setSarcasm(defaults.sarcasm);
  }

  const dirty =
    humor !== (source === 'saved' ? humor : defaults.humor) ||
    honesty !== (source === 'saved' ? honesty : defaults.honesty) ||
    sarcasm !== (source === 'saved' ? sarcasm : defaults.sarcasm);

  return (
    <section aria-labelledby="tars-panel" className="card overflow-hidden">
      <header className="space-y-1 border-b border-border-tertiary bg-bg-secondary px-4 py-3">
        <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
          tars · per-player calibration
        </p>
        <h2 id="tars-panel" className="font-serif text-lg">
          Tune Wilman&apos;s voice for your ear
        </h2>
        <p className="text-xs text-fg-secondary">
          Three sliders shape Wilman&apos;s tonal dial for content directed at you — verdicts,
          daily briefs, photo critiques, headliners. Static pre-rendered audio (coaching
          scenarios, restaurant intros, welcome briefings) uses the team default and is
          unaffected.
        </p>
      </header>

      <div className="space-y-4 px-4 py-4">
        {loading && (
          <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
            …loading
          </p>
        )}
        {error && (
          <div className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 font-mono text-[11px] text-danger">
            {error}
          </div>
        )}

        {!loading && (
          <>
            <Slider label="humor" value={humor} onChange={setHumor} hint="dryness vs warmth" />
            <Slider label="honesty" value={honesty} onChange={setHonesty} hint="diplomatic vs direct" />
            <Slider label="sarcasm" value={sarcasm} onChange={setSarcasm} hint="straight vs barbed" />

            <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
              currently · <span className="normal-case tracking-normal text-fg-secondary">
                {source === 'saved' ? 'saved' : `default for you (${defaults.humor}/${defaults.honesty}/${defaults.sarcasm})`}
              </span>
            </p>

            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={onPreview}
                disabled={previewState === 'rendering'}
                className="rounded-md border border-action bg-action px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-action-fg transition hover:bg-action-hover disabled:opacity-50"
              >
                {previewState === 'rendering' ? 'rendering…' : 'preview · 15 sec'}
              </button>
              <button
                type="button"
                onClick={onSave}
                disabled={savingState === 'saving'}
                className="rounded-md border border-copper/60 bg-copper/10 px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-copper-dark transition hover:bg-copper/20 disabled:opacity-50"
              >
                {savingState === 'saving' ? 'saving…' : savingState === 'saved' ? 'saved ✓' : 'save'}
              </button>
              <button
                type="button"
                onClick={onResetToDefault}
                disabled={!dirty && source !== 'saved'}
                className="rounded-md border border-border-tertiary bg-bg-secondary px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-fg-secondary transition hover:bg-bg-tertiary disabled:opacity-50"
              >
                reset to default
              </button>
            </div>

            {previewError && (
              <p className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 font-mono text-[11px] text-danger">
                preview failed — {previewError}
              </p>
            )}

            {previewUrl && (
              <audio
                controls
                preload="auto"
                src={previewUrl}
                autoPlay
                className="w-full"
                aria-label="TARS preview"
              />
            )}
          </>
        )}
      </div>
    </section>
  );
}

function Slider({ label, value, onChange, hint }) {
  return (
    <label className="block space-y-1">
      <div className="flex items-baseline justify-between">
        <span className="font-mono text-[10px] uppercase tracking-wider text-fg-secondary">
          {label}
        </span>
        <span className="font-mono text-sm text-fg-primary">{value}</span>
      </div>
      <input
        type="range"
        min={0}
        max={100}
        step={5}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-action"
        aria-label={label}
      />
      <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
        {hint}
      </p>
    </label>
  );
}
